// Per-contact consent from a client's own uploaded list.
// This is not Speak's website callback or marketing consent. Those rows live
// in marketing_consent_records and must not be passed here.
// No Deno APIs, so Vitest can import it.

import { canonicalDncPhone, dncNumbersMatch } from "./dncGuard.ts";

export const CLIENT_LIST_RECORD_TYPE = "client_list";
export const CONSENT_RETENTION_POLICY = "campaign_life_plus_6_years";
export const CONSENT_RETENTION_YEARS = 6;

export type ClientConsentReason =
  | "client_consent_required"
  | "client_consent_sender"
  | "client_consent_channel"
  | "client_consent_ai_voice"
  | "client_consent_sms";

export interface ClientConsentDecision {
  allowed: boolean;
  reason: ClientConsentReason | "ok";
  message: string;
}

export interface ClientListConsent {
  userId: string;
  campaignId: string;
  contactListId: string;
  phone: string;
  sender: string;
  channels: string[];
  aiVoice: boolean;
  source: string;
  consentedAt: string;
  wording: string;
  softOptIn: boolean;
  recordType: typeof CLIENT_LIST_RECORD_TYPE;
}

const OK: ClientConsentDecision = { allowed: true, reason: "ok", message: "" };

function text(value: unknown): string {
  return typeof value === "string" ? value.trim() : value == null ? "" : String(value).trim();
}

export function normalizePartyName(value: string | null | undefined): string {
  return (value ?? "").trim().replace(/\s+/g, " ").toLowerCase();
}

export function partyNamesMatch(left: string | null | undefined, right: string | null | undefined): boolean {
  const a = normalizePartyName(left);
  const b = normalizePartyName(right);
  return a.length > 0 && a === b;
}

function flag(value: unknown): boolean {
  if (value === true) return true;
  if (typeof value === "number") return value === 1;
  if (typeof value !== "string") return false;
  const raw = value.trim().toLowerCase();
  return raw === "true" || raw === "yes" || raw === "y" || raw === "1";
}

export function asClientListConsent(row: Record<string, unknown>): ClientListConsent {
  const channelsRaw = row.channels;
  const channels = Array.isArray(channelsRaw)
    ? channelsRaw.map((item) => text(item).toLowerCase()).filter((item) => item === "phone" || item === "sms")
    : [];
  return {
    userId: text(row.userId ?? row.user_id),
    campaignId: text(row.campaignId ?? row.campaign_id),
    contactListId: text(row.contactListId ?? row.contact_list_id),
    phone: text(row.phone ?? row.phone_number),
    sender: text(row.sender),
    channels: [...new Set(channels)],
    aiVoice: flag(row.aiVoice ?? row.ai_voice),
    source: text(row.source),
    consentedAt: text(row.consentedAt ?? row.consented_at),
    wording: text(row.wording),
    softOptIn: flag(row.softOptIn ?? row.soft_opt_in),
    recordType: CLIENT_LIST_RECORD_TYPE,
  };
}

function decision(reason: ClientConsentReason, message: string): ClientConsentDecision {
  return { allowed: false, reason, message };
}

function sameContact(record: ClientListConsent, input: {
  accountUserId: string;
  campaignId: string;
  contactListId: string;
  phone: string;
}): boolean {
  return record.recordType === CLIENT_LIST_RECORD_TYPE &&
    record.userId === input.accountUserId &&
    record.campaignId === input.campaignId &&
    record.contactListId === input.contactListId &&
    dncNumbersMatch(record.phone, input.phone);
}

/**
 * AI voice dial for a managed campaign. Only a client_list row for this
 * contact list counts. Sender must be the declaration's client legal name,
 * channels must include phone, and ai_voice must be true.
 */
export function assessClientVoiceCall(input: {
  records: Array<ClientListConsent | Record<string, unknown>> | null | undefined;
  phone: string;
  accountUserId: string;
  campaignId: string | null | undefined;
  contactListId: string | null | undefined;
  declarationSender: string | null | undefined;
}): ClientConsentDecision {
  if (!input.campaignId || !input.contactListId) {
    return decision(
      "client_consent_required",
      "This managed campaign has no current contact list, so the call was not placed.",
    );
  }
  if (!input.records) {
    return decision(
      "client_consent_required",
      "The client's per-contact consent could not be checked, so the call was not placed.",
    );
  }
  const records = input.records.map((row) => asClientListConsent(row as Record<string, unknown>));
  const mine = records.filter((record) => sameContact(record, {
    accountUserId: input.accountUserId,
    campaignId: input.campaignId ?? "",
    contactListId: input.contactListId ?? "",
    phone: input.phone,
  }));
  if (!mine.length) {
    return decision(
      "client_consent_required",
      "This contact has no client consent record on the current list, so the call was not placed.",
    );
  }
  const named = mine.filter((record) => partyNamesMatch(record.sender, input.declarationSender));
  if (!named.length) {
    return decision(
      "client_consent_sender",
      "The client consent record does not name the same client legal name as the signed declaration, so the call was not placed.",
    );
  }
  const phone = named.filter((record) => record.channels.includes("phone"));
  if (!phone.length) {
    return decision(
      "client_consent_channel",
      "SMS-only consent does not allow a call. The client record has to include phone.",
    );
  }
  if (!phone.some((record) => record.aiVoice)) {
    return decision(
      "client_consent_ai_voice",
      "The client consent record does not cover AI voice calls, so the call was not placed.",
    );
  }
  return OK;
}

/**
 * SMS for a managed campaign. Allowed when the client record includes sms,
 * or soft_opt_in is true (existing customer, opt-out offered).
 */
export function assessClientSms(input: {
  records: Array<ClientListConsent | Record<string, unknown>> | null | undefined;
  phone: string;
  accountUserId: string;
  campaignId: string | null | undefined;
  contactListId: string | null | undefined;
  declarationSender: string | null | undefined;
}): ClientConsentDecision {
  if (!input.campaignId || !input.contactListId) {
    return decision(
      "client_consent_sms",
      "This managed campaign has no current contact list, so the text was not sent.",
    );
  }
  if (!input.records) {
    return decision(
      "client_consent_sms",
      "The client's per-contact consent could not be checked, so the text was not sent.",
    );
  }
  const records = input.records.map((row) => asClientListConsent(row as Record<string, unknown>));
  const mine = records.filter((record) => sameContact(record, {
    accountUserId: input.accountUserId,
    campaignId: input.campaignId ?? "",
    contactListId: input.contactListId ?? "",
    phone: input.phone,
  }));
  const named = mine.filter((record) => partyNamesMatch(record.sender, input.declarationSender));
  if (!named.length) {
    return decision(
      "client_consent_sms",
      "This contact has no client consent record on the current list for this client, so the text was not sent.",
    );
  }
  if (named.some((record) => record.channels.includes("sms") || record.softOptIn)) return OK;
  return decision(
    "client_consent_sms",
    "The client consent record does not include SMS or a soft opt-in with an opt-out offered, so the text was not sent.",
  );
}

const CONSENT_COLUMNS =
  "user_id, campaign_id, contact_list_id, phone_number, sender, channels, ai_voice, source, consented_at, wording, soft_opt_in, record_type";

type ConsentQuery = {
  eq: (column: string, value: string) => ConsentQuery;
  then: PromiseLike<{ data: unknown; error: { message?: string } | null }>["then"];
};

export type ClientConsentLookup = {
  from: (table: string) => {
    select: (columns: string) => ConsentQuery;
  };
};

/**
 * One contact on the current list. The phone is stored and queried as E.164.
 * There is no row cap: a list longer than 50 must still find the contact.
 */
export async function loadClientConsentForPhone(
  supabase: ClientConsentLookup,
  input: { campaignId: string; contactListId: string; phone: string },
): Promise<{ rows: Record<string, unknown>[] } | { error: string }> {
  const phone = canonicalDncPhone(input.phone);
  if (!phone) return { error: "The phone number could not be normalised." };
  const { data, error } = await supabase
    .from("campaign_client_consent_records")
    .select(CONSENT_COLUMNS)
    .eq("campaign_id", input.campaignId)
    .eq("contact_list_id", input.contactListId)
    .eq("phone_number", phone);
  if (error) return { error: error.message || "The client consent record could not be loaded." };
  return { rows: Array.isArray(data) ? data as Record<string, unknown>[] : [] };
}

export interface ClientConsentStore {
  loadCampaign: (campaignId: string) => Promise<{ id: string; userId: string } | null | "error">;
  loadCurrentList: (campaignId: string) => Promise<{ id: string } | null | "error">;
  insertRecords: (rows: Record<string, unknown>[]) => Promise<{ error: string | null }>;
}

/**
 * Service-role insert of client-list consent. The caller must own the campaign,
 * and every row must belong to that campaign's current contact list.
 */
export async function storeCampaignClientConsent(
  deps: ClientConsentStore & { userId: string; body: Record<string, unknown> },
): Promise<{ ok: true; stored: number } | { ok: false; status: number; errors: string[] }> {
  const campaignId = text(deps.body.campaign_id);
  const contactListId = text(deps.body.contact_list_id);
  const rawRecords = deps.body.records;
  if (!campaignId || !contactListId) {
    return { ok: false, status: 400, errors: ["Campaign and contact list are required."] };
  }
  if (!Array.isArray(rawRecords) || rawRecords.length === 0) {
    return { ok: false, status: 400, errors: ["At least one client consent record is required."] };
  }

  const campaign = await deps.loadCampaign(campaignId);
  if (campaign === "error") return { ok: false, status: 503, errors: ["The campaign could not be checked."] };
  if (!campaign || campaign.userId !== deps.userId) {
    return { ok: false, status: 403, errors: ["This campaign is not on your account."] };
  }

  const current = await deps.loadCurrentList(campaignId);
  if (current === "error") return { ok: false, status: 503, errors: ["The current contact list could not be checked."] };
  if (!current || current.id !== contactListId) {
    return {
      ok: false,
      status: 409,
      errors: ["This contact list is no longer the current list. Upload it again before importing."],
    };
  }

  const stamped: Record<string, unknown>[] = [];
  for (const raw of rawRecords) {
    const row = stampClientConsentRow(raw, {
      userId: deps.userId,
      campaignId,
      contactListId,
    });
    if (row.ok === false) return { ok: false, status: 400, errors: [row.error] };
    stamped.push(row.row);
  }

  const inserted = await deps.insertRecords(stamped);
  if (inserted.error) {
    return { ok: false, status: 503, errors: ["Client consent records could not be stored, so nothing was imported."] };
  }
  return { ok: true, stored: stamped.length };
}

function stampClientConsentRow(
  raw: unknown,
  scope: { userId: string; campaignId: string; contactListId: string },
): { ok: true; row: Record<string, unknown> } | { ok: false; error: string } {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { ok: false, error: "A client consent record is malformed." };
  }
  const row = raw as Record<string, unknown>;
  if (text(row.campaign_id) !== scope.campaignId || text(row.contact_list_id) !== scope.contactListId) {
    return { ok: false, error: "A client consent record does not belong to this campaign's current list." };
  }
  const phone = canonicalDncPhone(text(row.phone_number));
  const sender = text(row.sender);
  const source = text(row.source);
  const wording = text(row.wording);
  const consentedAt = text(row.consented_at);
  const channels = Array.isArray(row.channels)
    ? row.channels.map((item) => text(item).toLowerCase()).filter((item) => item === "phone" || item === "sms")
    : [];
  if (!phone || !sender || normalizePartyName(sender) === "speak" || !source || !wording || !consentedAt) {
    return { ok: false, error: "A client consent record is missing its phone, sender, source, date, or wording." };
  }
  if (Number.isNaN(new Date(consentedAt).getTime())) {
    return { ok: false, error: "A client consent date is invalid." };
  }
  if (!channels.length || channels.length !== (Array.isArray(row.channels) ? row.channels.length : 0)) {
    return { ok: false, error: "Client consent channels must be phone, sms, or both." };
  }
  if (typeof row.ai_voice !== "boolean") return { ok: false, error: "ai_voice must be true or false." };
  return {
    ok: true,
    row: {
    user_id: scope.userId,
    campaign_id: scope.campaignId,
    contact_list_id: scope.contactListId,
    phone_number: phone,
    sender,
    channels: [...new Set(channels)],
    ai_voice: row.ai_voice,
    source,
    consented_at: new Date(consentedAt).toISOString(),
    wording,
    soft_opt_in: row.soft_opt_in === true,
    record_type: CLIENT_LIST_RECORD_TYPE,
    retention_policy: CONSENT_RETENTION_POLICY,
    created_by: scope.userId,
    },
  };
}
